import { TMessageKey } from "@/i18n/types.gen";
import { Language } from "@/i18n/languages";

export enum ModuleName {
  OVERVIEW = "OVERVIEW",
  SHAREHOLDER_REGISTER = "SHAREHOLDER_REGISTER",
  DBOT = "DBOT",
  ARCHIVE = "ARCHIVE",
  MESSAGING = "MESSAGING",
  OWNERSHIP_REGISTER = "OWNERSHIP_REGISTER",
}

export const moduleLabels: { [key in ModuleName]: TMessageKey } = {
  [ModuleName.OVERVIEW]: "overview",
  [ModuleName.SHAREHOLDER_REGISTER]: "header.shareholderBook",
  [ModuleName.DBOT]: "module.dbot",
  [ModuleName.ARCHIVE]: "header.archive",
  [ModuleName.MESSAGING]: "header.messaging",
  [ModuleName.OWNERSHIP_REGISTER]: "header.investments",
};

export type Module = {
  moduleId: string;
  moduleName: ModuleName;
  features: Feature[];
};

export type Feature = {
  featureId: string;
  featureName: FeatureName;
  isEditable: boolean;
  isHideable: boolean;
  isManageable: boolean;
  isViewable: boolean;
  showEdit: boolean;
  showManage: boolean;
};

export enum FeatureName {
  //overview
  dashboard = "dashboard",
  entityData = "entityData",
  articles = "articles",
  poas = "poas",
  ownership = "ownership",
  companyRoles = "companyRoles",
  companySettings = "companySettings",

  //shareholder register
  shareholderRegisterDashboard = "shareholderRegisterDashboard",
  shareholders = "shareholders",
  transactions = "transactions",
  shareClasses = "shareClasses",
  financialInstruments = "financialInstruments",
  capTable = "capTable",
  commitments = "commitments",
  shareholderRegisterStatement = "shareholderRegisterStatement",
  beneficialOwners = "beneficialOwners",

  //dbot
  freetext = "freetext",
  companyEvents = "companyEvents",
  liveGeneralMeeting = "liveGeneralMeeting",
  increaseOfShareCapital = "increaseOfShareCapital",

  //ownership register
  owners = "owners",
  ownersTransactions = "ownersTransactions",
  ownersShareClasses = "ownersShareClasses",

  //archive
  boardArchive = "boardArchive",
  shareholderArchive = "shareholderArchive",
  adminArchive = "adminArchive",
  customArchives = "customArchives",

  //messaging
  boardMessages = "boardMessages",
  shareholderMessages = "shareholderMessages",
}

export const featureLabels: { [key in FeatureName]: TMessageKey } = {
  [FeatureName.dashboard]: "common.dashboard",
  [FeatureName.entityData]: "header.entity-data",
  [FeatureName.articles]: "feature.articles",
  [FeatureName.poas]: "feature.poas",
  [FeatureName.ownership]: "feature.ownership",
  [FeatureName.companyRoles]: "feature.company-roles",
  [FeatureName.companySettings]: "feature.company-settings",

  [FeatureName.shareholderRegisterDashboard]: "common.dashboard",
  [FeatureName.shareholders]: "feature.shareholders",
  [FeatureName.transactions]: "feature.transactions",
  [FeatureName.shareClasses]: "feature.share-classes",
  [FeatureName.financialInstruments]: "header.options",
  [FeatureName.capTable]: "feature.cap-table",
  [FeatureName.commitments]: "feature.commitments",
  [FeatureName.shareholderRegisterStatement]: "feature.shareholder-register-statement",
  [FeatureName.beneficialOwners]: "feature.beneficial-owners",

  [FeatureName.freetext]: "feature.freetext",
  [FeatureName.companyEvents]: "feature.company-events",
  [FeatureName.liveGeneralMeeting]: "feature.live-general-meeting",
  [FeatureName.increaseOfShareCapital]: "feature.increase-of-share-capital",

  [FeatureName.owners]: "feature.shareholders",
  [FeatureName.ownersTransactions]: "feature.transactions",
  [FeatureName.ownersShareClasses]: "feature.share-classes",

  [FeatureName.boardArchive]: "feature.board-archive",
  [FeatureName.shareholderArchive]: "feature.shareholder-archive",
  [FeatureName.adminArchive]: "feature.admin-archive",
  [FeatureName.customArchives]: "feature.custom-archives",

  [FeatureName.boardMessages]: "feature.board-messages",
  [FeatureName.shareholderMessages]: "feature.shareholder-messages",
};

export enum BillInterval {
  MONTH = "MONTH",
  YEAR = "YEAR",
}

export type ProductPriceDescription = {
  productPriceId: string;
  priceName: string;
  price: number;
  currency: string;
  billInterval: BillInterval;
  tier?: string;
};

export type ProductLanguage = {
  language: Language;
  name: string;
  description?: string;
  tailoredPriceDescription?: string;
};

export enum ActivationMethod {
  SELF_SERVICE = "SELF_SERVICE",
  REQUEST_OFFER = "REQUEST_OFFER",
}

export type ProductDescription = {
  productId: string;
  productName: string;
  prices: ProductPriceDescription[];
  productLanguages: ProductLanguage[];
  activationMethod: ActivationMethod;
  useDarkMode: boolean;
  isPurchasableOnWeb: boolean;
};

export type PricingTier = {
  tier: string;
  type: TierType;
};
export type TierType = "shareholder_interval";
